/**
 * SRG production dialog domains and their per-business-unit metadata.
 *
 * Single source of truth for the hostname→BU mapping, consumed both by the
 * SSO login flow (`util/loginproviders.ts`, which picks the per-BU provider)
 * and by the per-language navbar logo config in `psi.config.ts`.
 * Kept dependency-free on purpose: `psi.config.ts` imports it at module
 * evaluation time, so importing it from `util/loginproviders.ts` directly
 * would create an import cycle (loginproviders → util/util → psi.config).
 */

// The SRG business unit's sign-in provider on each production hostname. `key`
// names a provider that the server configures with the BU's own OIDC client.
// Only production hostnames map here — staging hostnames (`testdialog*.ch`) and
// direct psi2.dev / psi1.dev access intentionally fall through to the `srgInt`
// provider in loginproviders.ts, which targets the integration environment
// (account-int.srgssr.ch).
export const SRG_BU_LOGIN_BY_HOSTNAME: Record<string, { key: string; name: string; uiLocale: string }> = {
    'dialogue.rts.ch': { key: 'srgRts', name: 'RTS', uiLocale: 'fr' },
    'dialog.srf.ch': { key: 'srgSrf', name: 'SRF', uiLocale: 'de' },
    'dialogo.rsi.ch': { key: 'srgRsi', name: 'RSI', uiLocale: 'it' },
    'dialog.rtr.ch': { key: 'srgRtr', name: 'RTR', uiLocale: 'rm' },
    'dialogue.swissinfo.ch': { key: 'srgSwi', name: 'SWI', uiLocale: 'en' },
};

/**
 * UI locale of each SRG production dialog domain, derived from the per-BU
 * login mapping above so the hostname→language relationship has a single
 * source of truth. Used by the srg silo config to pick the per-language
 * navbar logo.
 */
export const SRG_UI_LOCALE_BY_HOSTNAME: Record<string, string> = Object.fromEntries(
    Object.entries(SRG_BU_LOGIN_BY_HOSTNAME).map(([hostname, bu]) => [hostname, bu.uiLocale]),
);
